import { useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { LEGAL_TEMPLATES } from "@/features/legal";
import { getTemplateCategory } from "../consts";
import type { TemplateCategory } from "../consts";

export function useTemplatesPage() {
  const { t } = useTranslation("LegalTemplatesPage");

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategories, setSelectedCategories] = useState<Set<TemplateCategory>>(new Set());

  const handleCategoryToggle = useCallback(function handleCategoryToggle(category: TemplateCategory) {
    setSelectedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  }, []);

  const handleResetFilters = useCallback(function handleResetFilters() {
    setSearchQuery("");
    setSelectedCategories(new Set());
  }, []);

  const hasActiveFilters =
    searchQuery.length > 0 ||
    selectedCategories.size > 0;

  const filteredTemplates = useMemo(() => {
    let result = [...LEGAL_TEMPLATES];

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (tpl) =>
          t(tpl.titleKey).toLowerCase().includes(q) ||
          t(tpl.subtitleKey).toLowerCase().includes(q),
      );
    }

    if (selectedCategories.size > 0) {
      result = result.filter((tpl) =>
        selectedCategories.has(getTemplateCategory(tpl.id)),
      );
    }

    return result;
  }, [searchQuery, selectedCategories, t]);

  return {
    t,
    filteredTemplates,
    searchQuery,
    setSearchQuery,
    selectedCategories,
    handleCategoryToggle,
    handleResetFilters,
    hasActiveFilters,
  };
}
